import { loadPlacesLibrary } from "./googlePlacesLoader";
import {
  PLACES_DETAIL_FIELDS,
  PLACES_INCLUDED_REGION_CODES,
  PLACES_LANGUAGE,
  PLACES_LOCATION_BIAS_RADIUS_M,
  PLACES_REGION,
} from "../utils/consultOrder.constants";

// ITAM-2583 (phase-2): the injectable seam between the app and the Google Places
// Library (New). AddressCreate depends ONLY on these functions (never on the SDK
// directly), so tests mock this module with a fake element + fake resolver and no
// billable/network call is ever made.
//
// PRIMARY path = PlaceAutocompleteElement: it manages its own session token,
// renders Google's attribution, and ships the combobox a11y for free. The
// Data-API path (fetchAddressSuggestions) is a documented FALLBACK behind the
// same seam; the element path is the default.
//
// Field mask is the minimal Essentials set (location + formattedAddress +
// addressComponents) — NEVER displayName or any Pro/Enterprise field.

export interface ResolvedPlaceComponent {
  longText: string;
  shortText: string;
  types: string[];
}

export interface ResolvedPlace {
  lat: number;
  lng: number;
  formatted: string;
  components: ResolvedPlaceComponent[];
}

export interface CreatePlaceAutocompleteElementOptions {
  branchLat?: number;
  branchLng?: number;
}

/**
 * Builds a configured PlaceAutocompleteElement for Serbia. locationBias is a soft
 * circle around the branch coordinates ONLY when they are provided — we never
 * hard-code a Belgrade fallback (a null bias just lets Google use its defaults
 * within includedRegionCodes).
 */
export const createPlaceAutocompleteElement = async (
  opts: CreatePlaceAutocompleteElementOptions
): Promise<google.maps.places.PlaceAutocompleteElement> => {
  const { PlaceAutocompleteElement } = await loadPlacesLibrary();

  const elementOptions: google.maps.places.PlaceAutocompleteElementOptions = {
    includedRegionCodes: [...PLACES_INCLUDED_REGION_CODES],
    requestedLanguage: PLACES_LANGUAGE,
    requestedRegion: PLACES_REGION,
  };

  if (
    typeof opts.branchLat === "number" &&
    typeof opts.branchLng === "number" &&
    opts.branchLat !== 0 &&
    opts.branchLng !== 0
  ) {
    elementOptions.locationBias = {
      center: { lat: opts.branchLat, lng: opts.branchLng },
      radius: PLACES_LOCATION_BIAS_RADIUS_M,
    };
  }

  return new PlaceAutocompleteElement(elementOptions);
};

/**
 * Resolves a user-selected prediction into flat lat/lng + formatted + components.
 * Uses the minimal Essentials field mask only. The caller must still treat a
 * zero/NaN lat/lng as UNRESOLVED (see mapPlaceToUserAddress) — free text must
 * never become a submittable address.
 */
export const resolveSelectedPlace = async (
  placePrediction: google.maps.places.PlacePrediction
): Promise<ResolvedPlace> => {
  const place = placePrediction.toPlace();
  await place.fetchFields({ fields: [...PLACES_DETAIL_FIELDS] });

  const location = place.location;
  const components = (place.addressComponents ?? []).map((component) => ({
    longText: component.longText ?? "",
    shortText: component.shortText ?? "",
    types: component.types ?? [],
  }));

  return {
    lat: location ? location.lat() : NaN,
    lng: location ? location.lng() : NaN,
    formatted: place.formattedAddress ?? "",
    components,
  };
};

export interface FetchAddressSuggestionsOptions {
  branchLat?: number;
  branchLng?: number;
}

/**
 * Data-API FALLBACK: fetch prediction suggestions manually via
 * AutocompleteSuggestion.fetchAutocompleteSuggestions. Kept intentionally small —
 * the element path is the default. The caller owns/rotates the sessionToken and
 * MUST render Google attribution when it renders these suggestions itself.
 * Returned suggestions expose `.placePrediction`, which feeds resolveSelectedPlace.
 */
export const fetchAddressSuggestions = async (
  query: string,
  sessionToken: google.maps.places.AutocompleteSessionToken,
  opts: FetchAddressSuggestionsOptions = {}
): Promise<google.maps.places.AutocompleteSuggestion[]> => {
  const { AutocompleteSuggestion } = await loadPlacesLibrary();

  const request: google.maps.places.AutocompleteRequest = {
    input: query,
    sessionToken,
    includedRegionCodes: [...PLACES_INCLUDED_REGION_CODES],
    language: PLACES_LANGUAGE,
    region: PLACES_REGION,
  };

  if (
    typeof opts.branchLat === "number" &&
    typeof opts.branchLng === "number" &&
    opts.branchLat !== 0 &&
    opts.branchLng !== 0
  ) {
    request.locationBias = {
      center: { lat: opts.branchLat, lng: opts.branchLng },
      radius: PLACES_LOCATION_BIAS_RADIUS_M,
    };
  }

  const { suggestions } =
    await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
  return suggestions ?? [];
};
